import React, { useState } from 'react';
import { Image, ImageSourcePropType, TouchableOpacity, View } from 'react-native';
import { createStyleSheet, UnistylesRuntime, useStyles } from 'react-native-unistyles';
import { ScrollView } from 'react-native-web-hover';
import CustomButton from '../components/CustomButton';
import MyAppText from '../components/MyAppText';
import UserInformationModal from '../components/UserInformationModal';
import { useAuth } from '../context/Auth';
import { translate } from '../services/translate';
import { WindowInformation } from '../services/window-information';
import { useCurrentApp } from '../context/CurrentApp';

interface MenuComponentProps {
    navigation: Navigation;
    authenticatedUser?: AuthenticatedUser;
}

interface MenuItem {
    action: () => void;
    icon: ImageSourcePropType;
    text: string;
    disabled: boolean;
}

export default function Menu({ navigation }: MenuComponentProps) {
    const { styles } = useStyles(styleSheet);
    const windowInfo = WindowInformation();
    const [showDropDown, setShowDropDown] = useState(false);
    const { user: authenticatedUser } = useAuth();
    const { isParentApp, isAgentApp } = useCurrentApp();

    let menuItems: MenuItem[] = [];
    if (isAgentApp) {
        menuItems = [{
            action: () => navigation.navigate('AppAgent'),
            icon: require('../../assets/icon_app_agent.png'),
            text: translate('appAgent'),
            disabled: !(authenticatedUser?.isAdmin
                || authenticatedUser?.permissions.receive_occurrences
                || authenticatedUser?.permissions.search_people_by_picture
                || authenticatedUser?.permissions.app_agent_deac_access)
        }];
    } else {
        menuItems = [{
            action: () => navigation.navigate('Cameras'),
            icon: require('../../assets/icon_cameras.png'),
            text: translate('cameras'),
            disabled: false
        },
        {
            action: () => {
                if (
                    authenticatedUser?.isAdmin
                    || authenticatedUser?.permissions.facial_module_access
                    || authenticatedUser?.permissions.facial_can_watch_all_events
                ) {
                    return navigation.navigate('Facial');
                }

                if (authenticatedUser?.permissions.facial_events_wanted_person ||
                    authenticatedUser?.permissions.facial_events_missing_person
                ) {
                    return navigation.navigate('Facial', { screen: 'LiveEvents' });
                }

                if (authenticatedUser?.permissions.facial_alerts_missing_person ||
                    authenticatedUser?.permissions.facial_alerts_wanted_person
                ) {
                    return navigation.navigate('Facial', { screen: 'LiveAlerts' });
                }

                if (
                    authenticatedUser?.permissions.manage_wanted_person
                    || authenticatedUser?.permissions.manage_missing_person
                ) {
                    return navigation.navigate('Facial', { screen: 'PeopleList' });
                }

                if (authenticatedUser?.permissions.facial_manage_tags) {
                    return navigation.navigate('Facial', { screen: 'Markers' });
                }
            },
            icon: require('../../assets/icon_facial.png'),
            text: translate('facial'),
            disabled: !(
                authenticatedUser?.isAdmin
                || authenticatedUser?.permissions.facial_module_access
                || authenticatedUser?.permissions.manage_wanted_person
                || authenticatedUser?.permissions.manage_missing_person
                || authenticatedUser?.permissions.facial_manage_tags
                || authenticatedUser?.permissions.facial_events_wanted_person
                || authenticatedUser?.permissions.facial_events_missing_person
                || authenticatedUser?.permissions.facial_alerts_missing_person
                || authenticatedUser?.permissions.facial_alerts_wanted_person
                || authenticatedUser?.permissions.facial_can_watch_all_events
            )
        },
        {
            action: () => navigation.navigate('SmartSearchFacial'),
            icon: require('../../assets/icon_smart_search_facial.png'),
            text: translate('smartSearchFacial'),
            disabled: !(authenticatedUser?.isAdmin || authenticatedUser?.permissions.smart_search_module_access)
        },
        {
            action: () => {
                if (
                    authenticatedUser?.isAdmin
                    || authenticatedUser?.permissions.lpr_module_access
                    || authenticatedUser?.permissions.lpr_can_watch_all_events
                ) {
                    return navigation.navigate('LPR');
                }

                if (authenticatedUser?.permissions.lpr_edit_denylist ||
                    authenticatedUser?.permissions.lpr_edit_illegal_debris ||
                    authenticatedUser?.permissions.lpr_see_denylist ||
                    authenticatedUser?.permissions.lpr_see_illegal_debris
                ) {
                    return navigation.navigate('LPR', { screen: 'Plates' });
                }
            },
            icon: require('../../assets/icon_lpr.png'),
            text: translate('lpr'),
            disabled: !(
                authenticatedUser?.isAdmin
                || authenticatedUser?.permissions.lpr_module_access
                || authenticatedUser?.permissions.lpr_edit_denylist
                || authenticatedUser?.permissions.lpr_see_denylist
                || authenticatedUser?.permissions.lpr_edit_illegal_debris
                || authenticatedUser?.permissions.lpr_see_illegal_debris
                || authenticatedUser?.permissions.lpr_watch_unit_detections
                || authenticatedUser?.permissions.lpr_edit_detection
                || authenticatedUser?.permissions.lpr_can_watch_all_events
            )
        },
        {
            action: () => navigation.navigate('ClonedPlate'),
            icon: require('../../assets/icon_cloned_plate.png'),
            text: translate('clonedPlate'),
            disabled: !(authenticatedUser?.isAdmin || authenticatedUser?.permissions.cloned_plate_module_access)
        },
        {
            action: () => navigation.navigate('Events'),
            icon: require('../../assets/icon_events.png'),
            text: translate('events'),
            disabled: !(
                authenticatedUser?.isAdmin ||
                authenticatedUser?.permissions.events_module_access ||
                authenticatedUser?.permissions.events_manage_missing_face_occurrence ||
                authenticatedUser?.permissions.events_manage_not_allowed__face_occurrence ||
                authenticatedUser?.permissions.events_manage_wanted_face_occurrence ||
                authenticatedUser?.permissions.events_manage_wanted_plate_occurrence ||
                authenticatedUser?.permissions.events_manage_wanted_plate_by_mobile_agent_occurrence ||
                authenticatedUser?.permissions.events_manage_irregular_vehicle_occurrence ||
                authenticatedUser?.permissions.events_manage_clone_suspect_occurrence ||
                authenticatedUser?.permissions.events_manage_illegal_debris_occurrence ||
                authenticatedUser?.permissions.events_manage_area_invasion_occurrence ||
                authenticatedUser?.permissions.events_manage_mp_wanted_plate_occurrence ||
                authenticatedUser?.permissions.events_manage_mp_wanted_mobile_agent_plate_occurrence ||
                authenticatedUser?.permissions.events_manage_mp_wanted_face_occurrence ||
                authenticatedUser?.permissions.events_manage_alarm_center_occurrence ||
                authenticatedUser?.permissions.events_manage_guardian_app_occurrence ||
                authenticatedUser?.permissions.events_manage_school_app_occurrence ||
                authenticatedUser?.permissions.events_manage_sigrc_occurrence ||
                authenticatedUser?.permissions.events_manage_cortex_plate_occurrence ||
                authenticatedUser?.permissions.events_manage_manual_occurrence ||
                authenticatedUser?.permissions.events_manage_camera_depredation_occurrence
            )
        },
        {
            action: () => {
                if (
                    authenticatedUser?.isAdmin
                    || authenticatedUser?.permissions.dispatch_module_access
                    || authenticatedUser?.permissions.dispatch_manage_occurrences
                    || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_occurrences
                    || authenticatedUser?.permissions.reopen_occurrence
                    || authenticatedUser?.permissions.transfer_occurrence
                    || authenticatedUser?.permissions.dispatch_manage_occurrences_bulk_finish
                    || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_occurrences_bulk_finish
                ) {
                    return navigation.navigate('Dispatch', { screen: 'Occurrences' });
                }

                if (authenticatedUser?.permissions.dispatch_manage_city_property
                    || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_city_property) {
                    return navigation.navigate('Dispatch', { screen: 'CityProperties' });
                }

                if (authenticatedUser?.permissions.dispatch_manage_handcuff_reasons
                    || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_handcuff_reasons
                ) {
                    return navigation.navigate('Dispatch', { screen: 'HandcuffReasons' });
                }

                if (authenticatedUser?.permissions.dispatch_manage_occurrence_tags
                    || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_occurrence_tags
                ) {
                    return navigation.navigate('Dispatch', { screen: 'OccurrenceTags' });
                }

                if (authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_occurrence_types) {
                    return navigation.navigate('Dispatch', { screen: 'OccurrenceTypes' });
                }

                if (authenticatedUser?.permissions.dispatch_manage_equipments
                    || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_equipments
                ) {
                    return navigation.navigate('Dispatch', { screen: 'Equipments' });
                }

                if (authenticatedUser?.permissions.dispatch_download_report
                    || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_download_report
                    || authenticatedUser?.permissions.dispatch_generate_report
                    || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_reports
                ) {
                    return navigation.navigate('Dispatch', { screen: 'DispatchReports' });
                }

                if (authenticatedUser?.permissions.dispatch_manage_report_tags
                    || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_report_tags
                ) {
                    return navigation.navigate('Dispatch', { screen: 'ReportTags' });
                }

                if (authenticatedUser?.permissions.dispatch_manage_assignments
                    || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_assignments
                ) {
                    return navigation.navigate('Dispatch', { screen: 'Assignments' });
                }

                if (authenticatedUser?.permissions.dispatch_manage_vehicle_relations
                    || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_vehicle_relations
                ) {
                    return navigation.navigate('Dispatch', { screen: 'VehicleRelations' });
                }

                if (authenticatedUser?.permissions.dispatch_manage_person_relations
                    || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_person_relations
                ) {
                    return navigation.navigate('Dispatch', { screen: 'PersonRelations' });
                }

                if (authenticatedUser?.permissions.dispatch_manage_objects_categories
                    || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_objects_categories
                ) {
                    return navigation.navigate('Dispatch', { screen: 'ObjectsCategories' });
                }

                if (authenticatedUser?.permissions.dispatch_manage_team_names
                    || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_unit_names
                    || authenticatedUser?.permissions.dispatch_manage_acting_body_units_unit_names
                ) {
                    return navigation.navigate('Dispatch', { screen: 'TeamNames' });
                }

                if (authenticatedUser?.permissions.dispatch_manage_unit_programs
                    || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_unit_programs
                ) {
                    return navigation.navigate('Dispatch', { screen: 'TeamPrograms' });
                }

                if (authenticatedUser?.permissions.dispatch_manage_units
                    || authenticatedUser?.permissions.dispatch_manage_acting_body_units
                    || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_units
                    || authenticatedUser?.permissions.dispatch_pause_units
                ) {
                    return navigation.navigate('Dispatch', { screen: 'Units' });
                }

                if (authenticatedUser?.permissions.dispatch_manage_finish_categories
                    || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_finish_categories
                ) {
                    return navigation.navigate('Dispatch', { screen: 'FinishCategories' });
                }

                if (authenticatedUser?.permissions.dispatch_see_resume) {
                    return navigation.navigate('Dispatch', { screen: 'Resume' });
                }
                if (authenticatedUser?.permissions.dispatch_manage_police_stations) {
                    return navigation.navigate('Dispatch', { screen: 'PoliceStations' });
                }
                if (authenticatedUser?.permissions.dispatch_manage_providences ||
                    authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_providences
                ) {
                    return navigation.navigate('Dispatch', { screen: 'Providences' });
                }
                if (authenticatedUser?.permissions.dispatch_manage_unit_pause_reasons
                    || authenticatedUser?.permissions.dispatch_manage_acting_body_units_unit_pause_reasons
                    || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_unit_pause_reasons
                ) {
                    return navigation.navigate('Dispatch', { screen: 'UnitPauseReasons' });
                }

                if (authenticatedUser?.permissions.dispatch_manage_unit_finish_early_reasons
                    || authenticatedUser?.permissions.dispatch_manage_acting_body_units_unit_finish_early_reasons
                    || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_unit_finish_early_reasons
                ) {
                    return navigation.navigate('Dispatch', { screen: 'UnitFinishEarlyReasons' });
                }
                if (authenticatedUser?.permissions.dispatch_manage_unit_displaced_reasons ||
                    authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_unit_displaced_reasons
                ) {
                    return navigation.navigate('Dispatch', { screen: 'UnitDisplacedReasons' });
                }
            },
            icon: require('../../assets/icon_dispatch.png'),
            text: translate('dispatch'),
            disabled: !(
                authenticatedUser?.isAdmin
                || authenticatedUser?.permissions.dispatch_module_access
                || authenticatedUser?.permissions.dispatch_manage_occurrences
                || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_occurrences
                || authenticatedUser?.permissions.reopen_occurrence
                || authenticatedUser?.permissions.transfer_occurrence
                || authenticatedUser?.permissions.dispatch_manage_city_property
                || authenticatedUser?.permissions.dispatch_manage_handcuff_reasons
                || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_handcuff_reasons
                || authenticatedUser?.permissions.dispatch_manage_occurrence_tags
                || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_occurrence_tags
                || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_occurrence_types
                || authenticatedUser?.permissions.dispatch_manage_equipments
                || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_equipments
                || authenticatedUser?.permissions.dispatch_download_report
                || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_download_report
                || authenticatedUser?.permissions.dispatch_generate_report
                || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_reports
                || authenticatedUser?.permissions.dispatch_manage_report_tags
                || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_report_tags
                || authenticatedUser?.permissions.dispatch_manage_assignments
                || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_assignments
                || authenticatedUser?.permissions.dispatch_manage_vehicle_relations
                || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_vehicle_relations
                || authenticatedUser?.permissions.dispatch_manage_person_relations
                || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_person_relations
                || authenticatedUser?.permissions.dispatch_manage_objects_categories
                || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_objects_categories
                || authenticatedUser?.permissions.dispatch_manage_team_names
                || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_unit_names
                || authenticatedUser?.permissions.dispatch_manage_acting_body_units_unit_names
                || authenticatedUser?.permissions.dispatch_manage_unit_programs
                || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_unit_programs
                || authenticatedUser?.permissions.dispatch_manage_units
                || authenticatedUser?.permissions.dispatch_manage_acting_body_units
                || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_units
                || authenticatedUser?.permissions.dispatch_pause_units
                || authenticatedUser?.permissions.dispatch_manage_finish_categories
                || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_finish_categories
                || authenticatedUser?.permissions.dispatch_see_resume
                || authenticatedUser?.permissions.dispatch_manage_police_stations
                || authenticatedUser?.permissions.dispatch_manage_providences
                || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_providences
                || authenticatedUser?.permissions.dispatch_manage_unit_pause_reasons
                || authenticatedUser?.permissions.dispatch_manage_acting_body_units_unit_pause_reasons
                || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_unit_pause_reasons
                || authenticatedUser?.permissions.dispatch_manage_unit_finish_early_reasons
                || authenticatedUser?.permissions.dispatch_manage_acting_body_units_unit_finish_early_reasons
                || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_unit_finish_early_reasons
                || authenticatedUser?.permissions.dispatch_manage_unit_displaced_reasons
                || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_unit_displaced_reasons
                || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_city_property
                || authenticatedUser?.permissions.dispatch_manage_occurrences_bulk_finish
                || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_occurrences_bulk_finish
            )
        },
        {
            action: () => navigation.navigate('AppAgent'),
            icon: require('../../assets/icon_app_agent.png'),
            text: translate('appAgent'),
            disabled: !(
                authenticatedUser?.isAdmin
                || authenticatedUser?.permissions.receive_occurrences
                || authenticatedUser?.permissions.search_people_by_picture
                || authenticatedUser?.permissions.app_agent_deac_access
            )
        },
        {
            action: () => navigation.navigate('GuardianApp'),
            icon: require('../../assets/icon_guardian_app.png'),
            text: translate('guardianApp'),
            disabled: !(authenticatedUser?.isAdmin || authenticatedUser?.permissions.guardian_app_module_access)
        },
        {
            action: () => {
                if (authenticatedUser?.isAdmin ||
                    authenticatedUser?.permissions.access_edit_user_settings ||
                    authenticatedUser?.permissions.access_edit_user_acting_body_units ||
                    authenticatedUser?.permissions.access_edit_user_all_acting_bodies ||
                    authenticatedUser?.permissions.access_remove_user_all_acting_bodies ||
                    authenticatedUser?.permissions.access_remove_user_acting_body ||
                    authenticatedUser?.permissions.access_remove_user_acting_body_unit) {
                    return navigation.navigate('Access');
                }
                if (authenticatedUser?.permissions.access_manage_work_schedules ||
                    authenticatedUser?.permissions.access_manage_all_acting_bodies_work_schedules) {
                    return navigation.navigate('Access', {
                        screen: 'WorkSchedules'
                    });
                }
                if (authenticatedUser?.permissions.access_management_user_tags) {
                    return navigation.navigate('Access', {
                        screen: 'UserTags'
                    });
                }
                if (authenticatedUser?.permissions.access_edit_permission_level_settings
                    || authenticatedUser?.permissions.access_edit_permission_level_settings_acting_body_unit
                    || authenticatedUser?.permissions.access_edit_permission_level_settings_all_acting_bodies
                ) {
                    return navigation.navigate('Access', {
                        screen: 'PermissionLevels'
                    });
                }
                if (authenticatedUser?.permissions.access_edit_group_settings ||
                    authenticatedUser?.permissions.access_edit_group_settings_acting_body_unit ||
                    authenticatedUser?.permissions.access_edit_group_settings_all_acting_bodies
                ) {
                    return navigation.navigate('Access', {
                        screen: 'Groups'
                    });
                }
                if (authenticatedUser?.permissions.access_edit_turnstile) {
                    return navigation.navigate('Access', {
                        screen: 'Turnstiles'
                    });
                }
                if (authenticatedUser?.permissions.access_edit_platform_settings) {
                    return navigation.navigate('Access', {
                        screen: 'PlatformSettings'
                    });
                }
                if (authenticatedUser?.permissions.access_manage_acting_body) {
                    return navigation.navigate('Access', {
                        screen: 'ActingBodies'
                    });
                }
                if (authenticatedUser?.permissions.access_manage_installation_company) {
                    return navigation.navigate('Access', {
                        screen: 'InstallationCompanies'
                    });
                }
                if (authenticatedUser?.permissions.access_manage_internet_company) {
                    return navigation.navigate('Access', {
                        screen: 'InternetCompanies'
                    });
                }
                if (authenticatedUser?.permissions.access_manage_ptz_priorities) {
                    return navigation.navigate('Access', {
                        screen: 'PtzPriority'
                    });
                }
                if (authenticatedUser?.permissions.access_manage_allowed_ips) {
                    return navigation.navigate('Access', {
                        screen: 'AllowedIps'
                    });
                }
                if (authenticatedUser?.permissions.access_see_abuse_reports
                    || authenticatedUser?.permissions.access_manage_abuse_reports) {
                    return navigation.navigate('Access', {
                        screen: 'AbuseReports'
                    });
                }
                if (
                    authenticatedUser?.permissions.dispatch_module_access
                    || authenticatedUser?.permissions.access_manage_skills
                    || authenticatedUser?.permissions.access_manage_all_acting_bodies_skills
                ) {
                    return navigation.navigate('Access', { screen: 'Skills' });
                }

                if (authenticatedUser?.permissions.access_manage_holidays) {
                    return navigation.navigate('Access', {
                        screen: 'Holidays'
                    });
                }
                if (authenticatedUser?.permissions.manage_support_tickets) {
                    return navigation.navigate('Access', {
                        screen: 'SupportTickets'
                    });
                }
            },
            icon: require('../../assets/icon_access.png'),
            text: translate('access'),
            disabled: !(authenticatedUser?.isAdmin
                || authenticatedUser?.permissions.access_edit_user_settings
                || authenticatedUser?.permissions.access_edit_user_acting_body_units
                || authenticatedUser?.permissions.access_edit_user_all_acting_bodies
                || authenticatedUser?.permissions.access_management_user_tags
                || authenticatedUser?.permissions.access_edit_permission_level_settings
                || authenticatedUser?.permissions.access_edit_permission_level_settings_acting_body_unit
                || authenticatedUser?.permissions.access_edit_permission_level_settings_all_acting_bodies
                || authenticatedUser?.permissions.access_remove_user_all_acting_bodies
                || authenticatedUser?.permissions.access_edit_group_settings
                || authenticatedUser?.permissions.access_edit_group_settings_acting_body_unit
                || authenticatedUser?.permissions.access_edit_group_settings_all_acting_bodies
                || authenticatedUser?.permissions.access_edit_platform_settings
                || authenticatedUser?.permissions.access_edit_turnstile
                || authenticatedUser?.permissions.access_manage_acting_body
                || authenticatedUser?.permissions.access_manage_installation_company
                || authenticatedUser?.permissions.access_manage_internet_company
                || authenticatedUser?.permissions.access_manage_ptz_priorities
                || authenticatedUser?.permissions.access_manage_allowed_ips
                || authenticatedUser?.permissions.access_manage_work_schedules
                || authenticatedUser?.permissions.access_manage_all_acting_bodies_work_schedules
                || authenticatedUser?.permissions.access_manage_skills
                || authenticatedUser?.permissions.access_manage_all_acting_bodies_skills
                || authenticatedUser?.permissions.access_manage_holidays
                || authenticatedUser?.permissions.manage_support_tickets
                || authenticatedUser?.permissions.access_remove_user_acting_body
                || authenticatedUser?.permissions.access_remove_user_acting_body_unit
                || authenticatedUser?.permissions.access_manage_abuse_reports
                || authenticatedUser?.permissions.access_see_abuse_reports
            )
        },
        {
            action: () => navigation.navigate('CRM'),
            icon: require('../../assets/icon_crm.png'),
            text: 'CRM',
            disabled: !(authenticatedUser?.isAdmin
                || authenticatedUser?.permissions.crm_module_access
            )
        },
        {
            action: () => navigation.navigate('Logs'),
            icon: require('../../assets/icon_logs.png'),
            text: 'Logs',
            disabled: !(authenticatedUser?.isAdmin || authenticatedUser?.permissions.logs_module_access)
        },
        {
            action: () => {
                if (authenticatedUser?.isAdmin
                    || authenticatedUser?.permissions.dashboard_module_access
                    || authenticatedUser?.permissions.dashboard_see_occurrence) {
                    return navigation.navigate('Dashboard', {
                        screen: 'DashboardOccurrences'
                    });
                }

                if (authenticatedUser?.permissions.dashboard_manage_monitoring_plates_groups
                    || authenticatedUser?.permissions.dashboard_manage_monitoring_plates
                    || authenticatedUser?.permissions.dashboard_see_monitoring_plates
                ) {
                    return navigation.navigate('Dashboard', {
                        screen: 'DashboardPlateMonitoring'
                    });
                }

                if (authenticatedUser?.permissions.dashboard_see_cameras) {
                    return navigation.navigate('Dashboard', {
                        screen: 'DashboardCameras'
                    });
                }

                if (authenticatedUser?.permissions.dashboard_see_agents
                    || authenticatedUser?.permissions.dashboard_see_agents_acting_body_units
                    || authenticatedUser?.permissions.dashboard_see_agents_acting_bodies) {
                    return navigation.navigate('Dashboard', {
                        screen: 'DashboardAgents'
                    });
                }

                if (authenticatedUser?.isAdmin
                    || authenticatedUser?.permissions.dashboard_see_vehicles
                    || authenticatedUser?.permissions.dashboard_see_vehicles_acting_bodies
                    || authenticatedUser?.permissions.dashboard_see_vehicles_acting_body_units) {
                    return navigation.navigate('Dashboard', {
                        screen: 'DashboardVehicles'
                    });
                }
            },
            icon: require('../../assets/icon_dashboard.png'),
            text: translate('dashboard'),
            disabled: !(
                authenticatedUser?.isAdmin
                || authenticatedUser?.permissions.dashboard_module_access
                || authenticatedUser?.permissions.dashboard_see_occurrence
                || authenticatedUser?.permissions.dashboard_see_cameras
                || authenticatedUser?.permissions.dashboard_see_agents
                || authenticatedUser?.permissions.dashboard_see_agents_acting_bodies
                || authenticatedUser?.permissions.dashboard_see_agents_acting_body_units
                || authenticatedUser?.permissions.dashboard_see_vehicles
                || authenticatedUser?.permissions.dashboard_see_vehicles_acting_bodies
                || authenticatedUser?.permissions.dashboard_see_vehicles_acting_body_units
                || authenticatedUser?.permissions.dashboard_manage_monitoring_plates_groups
                || authenticatedUser?.permissions.dashboard_manage_monitoring_plates
                || authenticatedUser?.permissions.dashboard_see_monitoring_plates
            )
        },
        {
            action: () => navigation.navigate('Signatures'),
            icon: require('../../assets/icon_signatures.png'),
            text: translate('signatures'),
            disabled: !(authenticatedUser?.isAdmin ||
                authenticatedUser?.permissions.supervisor_sign_report ||
                authenticatedUser?.permissions.supervisor_sign_report_unit
            ),
        },
        {
            action: () => {
                if (authenticatedUser?.permissions.armory_manage_armory_shift ||
                    authenticatedUser?.permissions.armory_manage_acting_body_units_armory_shift ||
                    authenticatedUser?.permissions.armory_manage_all_acting_bodies_armory_shift ||
                    authenticatedUser?.permissions.armory_replace_in_charge_armourer
                ) {
                    return navigation.navigate('Armory', { screen: 'ArmoryShifts' });
                }
                if (authenticatedUser?.permissions.armory_assigned_as_armourer) {
                    return navigation.navigate('Armory', { screen: 'MyArmoryShifts' });
                }
            },
            icon: require('../../assets/icon_armory.png'),
            text: translate('armory'),
            disabled: !(authenticatedUser?.permissions.armory_manage_armory_shift ||
                authenticatedUser?.permissions.armory_manage_acting_body_units_armory_shift ||
                authenticatedUser?.permissions.armory_manage_all_acting_bodies_armory_shift ||
                authenticatedUser?.permissions.armory_assigned_as_armourer ||
                authenticatedUser?.permissions.armory_replace_in_charge_armourer
            ),
        },
        {
            action: () => {
                if (authenticatedUser?.permissions.deac_manage_dashboard ||
                    authenticatedUser?.permissions.deac_manage_acting_body_units_dashboard ||
                    authenticatedUser?.permissions.deac_manage_all_acting_bodies_dashboard
                ) {
                    return navigation.navigate('Deac', { screen: 'Dashboard' });
                }
                if (authenticatedUser?.permissions.deac_manage_acting_body_units_vacancies ||
                    authenticatedUser?.permissions.deac_manage_all_acting_bodies_vacancies ||
                    authenticatedUser?.permissions.deac_manage_vacancies ||
                    authenticatedUser?.permissions.deac_visualize_vacancies
                ) {
                    return navigation.navigate('Deac', { screen: 'Vacancies' });
                }
                if (authenticatedUser?.permissions.deac_manage_applications ||
                    authenticatedUser?.permissions.deac_manage_acting_body_units_applications ||
                    authenticatedUser?.permissions.deac_manage_all_acting_bodies_applications
                ) {
                    return navigation.navigate('Deac', { screen: 'Applications' });
                }
                if (authenticatedUser?.permissions.deac_manage_acting_body_units_vacancies_templates ||
                    authenticatedUser?.permissions.deac_manage_all_acting_bodies_vacancies_templates ||
                    authenticatedUser?.permissions.deac_manage_vacancies_templates
                ) {
                    return navigation.navigate('Deac', { screen: 'VacanciesTemplates' });
                }
                if (authenticatedUser?.permissions.deac_manage_presence_control ||
                    authenticatedUser?.permissions.deac_manage_acting_body_units_presence_control ||
                    authenticatedUser?.permissions.deac_manage_all_acting_bodies_presence_control ||
                    authenticatedUser?.permissions.deac_manage_always_fix_presence
                ) {
                    return navigation.navigate('Deac', { screen: 'PresenceControl' });
                }
                if (authenticatedUser?.permissions.deac_manage_acting_body_vacancy_level ||
                    authenticatedUser?.permissions.deac_manage_all_acting_bodies_vacancy_level) {
                    return navigation.navigate('Deac', { screen: 'VacancyLevels' });
                }
                if (authenticatedUser?.permissions.deac_manage_workplaces ||
                    authenticatedUser?.permissions.deac_manage_acting_body_units_workplaces ||
                    authenticatedUser?.permissions.deac_manage_all_acting_bodies_workplaces) {
                    return navigation.navigate('Deac', { screen: 'Workplaces' });
                }
                if (authenticatedUser?.permissions.deac_manage_vacancy_tags) {
                    return navigation.navigate('Deac', { screen: 'VacancyTags' });
                }
                if (authenticatedUser?.permissions.deac_manage_vacancy_cancellation_reasons) {
                    return navigation.navigate('Deac', { screen: 'VacancyCancellationReasons' });
                }
                if (authenticatedUser?.permissions.deac_manage_acting_body_units_reports ||
                    authenticatedUser?.permissions.deac_manage_all_acting_bodies_reports) {
                    return navigation.navigate('Deac', { screen: 'DeacReports' });
                }
            },
            icon: require('../../assets/icon_deac.png'),
            text: translate('deac'),
            disabled: !(authenticatedUser?.permissions.deac_manage_acting_body_vacancy_level ||
                authenticatedUser?.permissions.deac_manage_all_acting_bodies_vacancy_level ||
                authenticatedUser?.permissions.deac_manage_vacancy_tags ||
                authenticatedUser?.permissions.deac_manage_workplaces ||
                authenticatedUser?.permissions.deac_manage_acting_body_units_workplaces ||
                authenticatedUser?.permissions.deac_manage_all_acting_bodies_workplaces ||
                authenticatedUser?.permissions.deac_manage_vacancies ||
                authenticatedUser?.permissions.deac_visualize_vacancies ||
                authenticatedUser?.permissions.deac_manage_acting_body_units_vacancies ||
                authenticatedUser?.permissions.deac_manage_all_acting_bodies_vacancies ||
                authenticatedUser?.permissions.deac_manage_presence_control ||
                authenticatedUser?.permissions.deac_manage_acting_body_units_presence_control ||
                authenticatedUser?.permissions.deac_manage_all_acting_bodies_presence_control ||
                authenticatedUser?.permissions.deac_manage_acting_body_units_reports ||
                authenticatedUser?.permissions.deac_manage_all_acting_bodies_reports ||
                authenticatedUser?.permissions.deac_manage_dashboard ||
                authenticatedUser?.permissions.deac_manage_acting_body_units_dashboard ||
                authenticatedUser?.permissions.deac_manage_all_acting_bodies_dashboard ||
                authenticatedUser?.permissions.deac_manage_acting_body_units_vacancies_templates ||
                authenticatedUser?.permissions.deac_manage_all_acting_bodies_vacancies_templates ||
                authenticatedUser?.permissions.deac_manage_vacancies_templates ||
                authenticatedUser?.permissions.deac_manage_vacancy_cancellation_reasons ||
                authenticatedUser?.permissions.deac_manage_applications ||
                authenticatedUser?.permissions.deac_manage_acting_body_units_applications ||
                authenticatedUser?.permissions.deac_manage_all_acting_bodies_applications ||
                authenticatedUser?.permissions.deac_manage_always_fix_presence
            ),
        }, {
            action: () => {
                if (authenticatedUser?.permissions.third_party_approved_companies_manage_inscriptions) {
                    return navigation.navigate('ThirdPartyApprovedCompanies', { screen: 'InscriptionsManagement' });
                }

                if (authenticatedUser?.permissions.manage_third_party_donated_cameras) {
                    return navigation.navigate('ThirdPartyApprovedCompanies', { screen: 'DonatedCamerasList' });
                }
            },
            icon: require('../../assets/icon_approved_companies.png'),
            text: translate('approvedCompanies'),
            disabled: !(authenticatedUser?.permissions.third_party_approved_companies_manage_inscriptions ||
                authenticatedUser?.permissions.manage_third_party_donated_cameras
            )
        }, {
            action: () => {
                if (authenticatedUser?.permissions.citizen_management) {
                    return navigation.navigate('Citizen', { screen: 'CitizenManagement' });
                }
                if (authenticatedUser?.permissions.citizen_watch_lpr_events) {
                    return navigation.navigate('Citizen', { screen: 'CitizenLprEvents' });
                }
                if (authenticatedUser?.permissions.citizen_management_banishment) {
                    return navigation.navigate('Citizen', { screen: 'CitizenBlockReasons' });
                }
            },
            icon: require('../../assets/icon_sentinel_app.png'),
            text: translate('citizenApp'),
            disabled: !(
                authenticatedUser?.permissions.citizen_management
                || authenticatedUser?.permissions.citizen_watch_lpr_events
                || authenticatedUser?.permissions.citizen_management_banishment
            )
        }];
    }

    if (isParentApp) {
        menuItems.push({
            action: () => navigation.navigate('WebRTC'),
            icon: require('../../assets/icon_webrtc.png'),
            text: translate('webrtc'),
            disabled: false
        });
    }

    if (isParentApp && authenticatedUser?.isAdmin) {
        menuItems.push({
            action: () => navigation.navigate('StreamServers'),
            icon: require('../../assets/icon_stream_servers.png'),
            text: translate('streamServers'),
            disabled: false
        });
    }

    return (
        <>
            <View style={styles.container}>
                <View style={{ paddingHorizontal: windowInfo.width < 580 ? 10 : 80 }}>
                    {authenticatedUser ?
                        <MyAppText style={styles.welcomeText}>{translate('welcome') + ', ' + (authenticatedUser.role ? `${authenticatedUser.role.name} ` : '') + (authenticatedUser.warName || authenticatedUser.name) + (authenticatedUser.actingBodyUnit?.name ? ' - ' + authenticatedUser.actingBodyUnit?.name : '') + '.'}</MyAppText>
                        : <></>
                    }
                </View>
                <ScrollView contentContainerStyle={styles.scrollContent(windowInfo.width < 580)}>
                    {menuItems.filter(item => !item.disabled).map((item, itemIndex) => (
                        <TouchableOpacity key={itemIndex} onPress={item.action} style={styles.card(windowInfo.width)}>
                            <View style={styles.iconBox}>
                                <Image source={item.icon} style={styles.icon} />
                            </View>
                            <View style={styles.textContent}>
                                <MyAppText style={styles.text}>{item.text}</MyAppText>
                                <CustomButton onPress={item.action} text={translate('starting')} type='secondary' />
                            </View>
                        </TouchableOpacity>
                    ))}
                </ScrollView>
            </View>
            <UserInformationModal
                showDropDown={showDropDown}
                setShowDropDown={setShowDropDown}
                navigation={navigation}
            />
        </>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        rowGap: 40,
        paddingTop: 40
    },
    welcomeText: {
        fontSize: 24,
        fontWeight: 'bold',
        color: theme.colors.labelColor
    },
    card: (width: number) => ({
        backgroundColor: theme.colors.container.background,
        borderRadius: 4,
        flexDirection: 'row',
        alignItems: 'center',
        padding: 15,
        height: 90,
        gap: 15,
        width: width < 406 ? 335 : 406,
        borderWidth: 1,
        borderColor: theme.colors.container.border
    }),
    iconBox: {
        height: 54,
        width: 54,
        borderRadius: 4,
        backgroundColor: UnistylesRuntime.themeName === 'light' ? '#000028' : '#222426',
        justifyContent: 'center',
        alignItems: 'center'
    },
    icon: {
        height: 36,
        width: 36,
        objectFit: 'contain',
    },
    text: {
        color: theme.colors.labelColor,
        fontSize: 16,
        fontWeight: 'bold',
        whiteSpace: 'break-spaces'
    },
    scrollContent: (smallPadding: boolean) => ({
        paddingHorizontal: smallPadding ? 10 : 80,
        rowGap: 20,
        columnGap: 16,
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'flex-start'
    }),
    textContent: {
        flexDirection: 'row',
        alignItems: 'center',
        flex: 1,
        justifyContent: 'space-between'
    }
}));
